/**
 * Profile → Interview settings: the org-wide Behavioral questions switch (0111). On → every AI
 * interview plan generated from now on carries these questions right after the opener, on every
 * requirement, without adding them job by job. Super admin only.
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MAX_BEHAVIORAL_QUESTIONS, type BehavioralSetting } from "@talenthop/shared";
import { api } from "../lib/api.js";
import { Alert, CheckBig } from "../components/icons.js";
import { apiErrorText } from "./admin/adminUi.js";
import { SettingSwitch } from "./Profile.js";

interface Res {
  setting: BehavioralSetting;
  defaults: string[];
}

export function BehavioralQuestionsCard({ orgId }: { orgId: string }): ReactNode {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["behavioral-questions", orgId],
    queryFn: () => api.get<Res>(`/orgs/${orgId}/behavioral-questions`),
    retry: 1,
  });
  const [enabled, setEnabled] = useState(false);
  const [questions, setQuestions] = useState<string[]>([]);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!q.data || hydrated) return;
    setEnabled(q.data.setting.enabled);
    setQuestions(q.data.setting.questions.length ? q.data.setting.questions : [...q.data.defaults]);
    setHydrated(true);
  }, [q.data, hydrated]);

  const save = useMutation({
    mutationFn: (next: BehavioralSetting) => api.put<Res>(`/orgs/${orgId}/behavioral-questions`, next),
    onSuccess: (res) => queryClient.setQueryData(["behavioral-questions", orgId], res),
  });
  const cleaned = questions.map((s) => s.trim()).filter((s) => s.length >= 4);
  const stored = q.data?.setting;
  const dirty =
    !!stored &&
    (stored.enabled !== enabled ||
      JSON.stringify(stored.questions.length ? stored.questions : q.data!.defaults) !== JSON.stringify(cleaned));

  return (
    <div className="card">
      <div className="card-h">
        <h3>Interview settings</h3>
        <span className="pill t">Whole organization</span>
      </div>
      <div className="card-b">
        {q.isLoading && <p className="mut sm">Loading…</p>}
        {q.isError && (
          <div className="banner crit mb-3">
            <Alert />
            <span className="flex-1">{apiErrorText(q.error, "Couldn't load the interview settings.")}</span>
            <button type="button" className="btn ghost sm" onClick={() => q.refetch()}>Retry</button>
          </div>
        )}
        {hydrated && (
          <>
            <div className="t1s-switch-row">
              <div className="t1s-switch-text">
                <div className="t1s-switch-title">Behavioral questions</div>
                <div className="t1s-switch-hint">
                  Every AI interview includes these, right after the opening question — on all requirements, no need to
                  add them job by job. Applies to question sets generated from now on; Regenerate a requirement's
                  questions to add them there. Answers are judged on what was said, never on tone or personality.
                </div>
              </div>
              <SettingSwitch label="Behavioral questions" on={enabled} disabled={save.isPending} onToggle={() => setEnabled((v) => !v)} />
            </div>
            {enabled && (
              <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                {questions.map((text, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <textarea
                      className="input"
                      rows={2}
                      maxLength={1000}
                      aria-label={`Behavioral question ${i + 1}`}
                      value={text}
                      onChange={(e) => setQuestions((qs) => qs.map((x, j) => (j === i ? e.target.value : x)))}
                      style={{ flex: 1, resize: "vertical" }}
                    />
                    <button
                      type="button"
                      className="btn ghost sm"
                      aria-label={`Remove question ${i + 1}`}
                      onClick={() => setQuestions((qs) => qs.filter((_, j) => j !== i))}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {questions.length < MAX_BEHAVIORAL_QUESTIONS && (
                  <button type="button" className="btn ghost sm" style={{ justifySelf: "start" }} onClick={() => setQuestions((qs) => [...qs, ""])}>
                    + Add question
                  </button>
                )}
                {cleaned.length === 0 && (
                  <p className="mut sm" style={{ margin: 0 }}>No questions written — the two standard behavioral questions will be used.</p>
                )}
              </div>
            )}
            {save.isSuccess && !dirty && (
              <div className="banner info mb-3" style={{ marginTop: 12 }}>
                <CheckBig />
                <span>Saved.</span>
              </div>
            )}
            {save.isError && (
              <div className="banner crit mb-3" style={{ marginTop: 12 }}>
                <Alert />
                <span>{apiErrorText(save.error, "Couldn't save. Please retry.")}</span>
              </div>
            )}
            <button
              className="btn primary"
              style={{ marginTop: 12 }}
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate({ enabled, questions: cleaned })}
            >
              {save.isPending ? "Saving…" : "Save interview settings"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
